import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Calendar } from "@/components/ui/calendar";

function formatDay(date, withYear) {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

// Compact label: "Jun 4 – Jun 7", or a single day when from === to (mid-selection),
// adding the year only when the endpoints straddle a year boundary so the trigger stays short.
export function formatRangeLabel(range) {
  const from = range?.from;
  const to = range?.to;
  if (!from) return "";
  if (!to || from.getTime() === to.getTime()) {
    return formatDay(from, from.getFullYear() !== new Date().getFullYear());
  }
  const spanYears = from.getFullYear() !== to.getFullYear();
  return `${formatDay(from, spanYears)} – ${formatDay(to, spanYears)}`;
}

/**
 * Resolve a range-mode day click into the familiar two-step flow, driven by the clicked day so
 * it behaves consistently regardless of react-day-picker v9's defaults (which both expand a
 * complete range and treat the first click from an empty selection as a complete single day):
 *   - no range yet, or a complete range → start a new range at the clicked day (end pending);
 *   - a start with no end → set the end (ordered), completing the range.
 * Returns the next range plus whether both endpoints are now set (so callers can close the
 * popover). Shared by {@link DateRangePicker} and the thread "last message" filter.
 */
export function applyRangeSelection(range, triggerDate) {
  if (!triggerDate) return { range, complete: false };
  if (!range?.from || (range.from && range.to)) {
    return { range: { from: triggerDate, to: undefined }, complete: false };
  }
  const [from, to] =
    triggerDate < range.from ? [triggerDate, range.from] : [range.from, triggerDate];
  return { range: { from, to }, complete: true };
}

/**
 * Single-field date range picker. Opens one calendar; the first click sets the range
 * start, the second sets the end (react-day-picker `mode="range"`). `setRange` receives
 * a `{ from?: Date, to?: Date }` object (or undefined when the selection is cleared).
 */
export function DateRangePicker({
  range,
  setRange,
  className,
  placeholder = "Select date range",
  numberOfMonths = 1,
  align = "start",
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const label = React.useMemo(() => formatRangeLabel(range), [range]);

  // Two-step start→end selection (see applyRangeSelection); close once both ends are chosen.
  const handleSelect = (_nextRange, triggerDate) => {
    const { range: resolved, complete } = applyRangeSelection(range, triggerDate);
    setRange(resolved);
    if (complete) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm justify-start text-left font-normal md:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            !range?.from && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
          {label ? label : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="range"
          selected={range}
          onSelect={handleSelect}
          numberOfMonths={numberOfMonths}
          defaultMonth={range?.from}
          autoFocus={false}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
